import { toSeoCanonicalUrl } from './seoSiteUrl';
import {
  publicRouteMetadata,
  dynamicRoutePatterns,
  type RouteMetadata,
} from '../config/seoConfig';

export interface BreadcrumbItem {
  name: string;
  url: string;
  position: number;
}

export interface BreadcrumbListSchema {
  '@context': string;
  '@type': string;
  itemListElement: Array<{
    '@type': string;
    position: number;
    name: string;
    item: string;
  }>;
}

interface RawBreadcrumbItem {
  name: string;
  url: string;
}

/**
 * Generates a human-readable label from a URL pathname.
 * Used as fallback for routes not in metadata.
 * Example: '/help-center' -> 'Help Center'
 */
function humanizePathname(pathname: string): string {
  if (!pathname || pathname === '/') return 'Home';

  return pathname
    .split('/')
    .filter((segment) => segment.length > 0)
    .map((segment) =>
      segment
        .split('-')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    )
    .join(' > ');
}

/**
 * Finds a dynamic route pattern match for the given pathname.
 */
function findDynamicRouteMatch(
  pathname: string
): (typeof dynamicRoutePatterns)[keyof typeof dynamicRoutePatterns] | null {
  for (const pattern of Object.values(dynamicRoutePatterns)) {
    if (pattern.pattern.test(pathname)) {
      return pattern;
    }
  }
  return null;
}

/**
 * Normalizes pathname by removing trailing slashes.
 */
function normalizePath(path: string): string {
  return path.replace(/\/$/, '') || '/';
}

/**
 * Gets metadata for a specific route from the registry.
 */
function getRouteMetadata(normalizedPath: string): RouteMetadata | null {
  return publicRouteMetadata[normalizedPath] || null;
}

/**
 * Collects a route metadata chain from the top-level parent to the given path.
 */
function getRouteMetadataChain(path: string): RouteMetadata[] {
  const visited = new Set<string>();
  const chain: RouteMetadata[] = [];
  let currentPath: string | undefined = path;

  while (currentPath && currentPath !== '/' && !visited.has(currentPath)) {
    visited.add(currentPath);
    const metadata = getRouteMetadata(currentPath);
    if (!metadata) {
      break;
    }

    chain.push(metadata);
    currentPath = metadata.parent;
  }

  return chain.reverse();
}

function withSequentialPositions(items: RawBreadcrumbItem[]): BreadcrumbItem[] {
  return items.map((item, index) => ({
    ...item,
    position: index + 1,
  }));
}

/**
 * Generates a breadcrumb trail for the current route.
 * Handles:
 * - Static routes from publicRouteMetadata
 * - Dynamic routes (/:id patterns) with parent routes
 * - Home page as root
 * - Fallback humanization for unmapped routes
 *
 * @param pathname Current pathname from useLocation
 * @returns Array of breadcrumb items in order
 */
export function generateBreadcrumbs(pathname: string): BreadcrumbItem[] {
  const normalized = normalizePath(pathname);

  if (normalized === '/') {
    return [
      {
        name: 'Home',
        url: toSeoCanonicalUrl('/'),
        position: 1,
      },
    ];
  }

  const breadcrumbs: RawBreadcrumbItem[] = [
    {
      name: 'Home',
      url: toSeoCanonicalUrl('/'),
    },
  ];

  const pushIfMissing = (item: RawBreadcrumbItem) => {
    const last = breadcrumbs[breadcrumbs.length - 1];
    if (!last || last.url !== item.url) {
      breadcrumbs.push(item);
    }
  };

  const dynamicMatch = findDynamicRouteMatch(normalized);

  if (dynamicMatch) {
    const parentChain = getRouteMetadataChain(dynamicMatch.parentPath);
    parentChain.forEach((parent) => {
      pushIfMissing({
        name: parent.label,
        url: toSeoCanonicalUrl(parent.path),
      });
    });

    pushIfMissing({
      name: dynamicMatch.getLabel(),
      url: toSeoCanonicalUrl(normalized),
    });

    return withSequentialPositions(breadcrumbs);
  }

  const staticChain = getRouteMetadataChain(normalized);
  if (staticChain.length > 0) {
    staticChain.forEach((item) => {
      pushIfMissing({
        name: item.label,
        url: toSeoCanonicalUrl(item.path),
      });
    });

    return withSequentialPositions(breadcrumbs);
  }

  pushIfMissing({
    name: humanizePathname(normalized),
    url: toSeoCanonicalUrl(normalized),
  });

  return withSequentialPositions(breadcrumbs);
}

/**
 * Generates a JSON-LD BreadcrumbList schema from breadcrumb items.
 * @param breadcrumbs Array of breadcrumb items
 * @returns BreadcrumbList schema object
 */
export function generateBreadcrumbListSchema(
  breadcrumbs: BreadcrumbItem[]
): BreadcrumbListSchema {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map((item) => ({
      '@type': 'ListItem',
      position: item.position,
      name: item.name,
      item: item.url,
    })),
  };
}

/**
 * Main entry point for breadcrumb schema generation.
 * Takes a pathname and returns the complete BreadcrumbList schema.
 * @param pathname Current pathname
 * @returns BreadcrumbList schema ready for JSON-LD
 */
export function getBreadcrumbListSchema(
  pathname: string
): BreadcrumbListSchema {
  const breadcrumbs = generateBreadcrumbs(pathname);
  return generateBreadcrumbListSchema(breadcrumbs);
}
